import {
    ChevronLeftIcon,
    ChevronRightIcon,
    CommentIcon,
    EllipsisIcon,
    ExportIcon,
    InfinityIcon,
    PrintIcon,
} from "@xola/icons";
import { Popover, Skeleton, Spinner } from "@xola/ui-kit";
import { AgGridReact } from "ag-grid-react";
import clsx from "clsx";
import { DateFormat, getWeekStartEndRange } from "common";
import dayjs from "dayjs";
import { sumBy } from "lodash";
import React, { memo, useState } from "react";
import { Experience, OPEN_UNLIMITED, Permission, Timeslot } from "types";
import { useAuthorization } from "../../hooks/useAuthorization";
import { Menu } from "../Common/Menu";
import { NoTranslate } from "../Common/NoTranslate";
import { SecondaryButton } from "../Common/SecondaryButton";
import { ExportRosterModal } from "../Event/ExportRosterModal";
import { MessageDayEventsModal } from "../Event/MessageDayEventsModal";
import { DashboardDatePickerPopover } from "./DashboardDatePickerPopover";
import { useDashboardTablePrint } from "./DashboardTable/useDashboardTablePrint";
import { useDashboardTableStore } from "./DashboardTable/useDashboardTableStore";
import { DashboardTableConfiguration } from "./DashboardTableConfiguration";
import { getBookedCount, getHoldStatusCount, getOpenCapacity } from "@/models/Timeslot";
import {
    DateNavigationDirection,
    isCalendarView,
    isDayView,
    isListView,
    isTableView,
    showDateNavButtons,
    useDashboardFilterStore,
} from "@/stores/dashboardFilterStore";
import { DashboardMode } from "@/types/Dashboard";

interface DashboardSummaryHeaderProps {
    date: string;
    timeslots: Timeslot[];
    isCollapsed: boolean;
    tableGridRef?: React.RefObject<AgGridReact>;
    visibleExperiences?: Experience[];
    onClick?: () => void;
    handleCustomButtonClick?: (direction: DateNavigationDirection) => void;
    onChange?: (date: Date) => void;
}

export const DashboardSummaryHeader = memo<DashboardSummaryHeaderProps>(
    ({
        date,
        timeslots,
        isCollapsed,
        tableGridRef,
        visibleExperiences,
        onClick,
        handleCustomButtonClick,
        onChange,
        ...rest
    }) => {
        const [showMessageDayEventsModal, setShowMessageDayEventsModal] = useState(false);
        const [showExportRosterModal, setShowExportRosterModal] = useState(false);
        const currentDate = dayjs(date);
        const holdStatusCount = sumBy(timeslots, (slot) => getHoldStatusCount(slot.statusCounts));
        const reservedCount = sumBy(timeslots, getBookedCount);
        const waitlistCount = sumBy(timeslots, "waitlistCount") ?? 0;

        const isLoading = useDashboardTableStore((state) => state.isLoading);

        const isList = isListView();
        const isDay = isDayView();
        const isTable = isTableView();
        const isCalendar = isCalendarView();
        const isTableOrCalendar = showDateNavButtons();

        let allTimeslotsHaveUnlimitedOpen = true;
        let availableCount = sumBy(timeslots, (slot) => {
            const open = getOpenCapacity(slot);
            allTimeslotsHaveUnlimitedOpen = allTimeslotsHaveUnlimitedOpen && open >= OPEN_UNLIMITED;
            return open >= OPEN_UNLIMITED ? 0 : open;
        });
        if (allTimeslotsHaveUnlimitedOpen && timeslots.length > 0) {
            availableCount = OPEN_UNLIMITED;
        }

        const eventIds = timeslots
            .filter((timeslot) => {
                if (!timeslot?.event) {
                    return false;
                }

                return getBookedCount(timeslot) > 0;
            })
            .map((timeslot) => timeslot?.event?.id);

        return (
            <div
                className={clsx(
                    "ui-dashboard-header-item",
                    "flex h-[45px] items-center",
                    isCollapsed ? "justify-between px-4" : "px-4 xl:px-6",
                    isTableOrCalendar
                        ? "border-t border-gray-dark/20 bg-gray-lighter"
                        : "border-b border-blue-light/10",
                )}
                {...rest}
            >
                <DateNav
                    isCollapsed={isCollapsed}
                    handleCustomButtonClick={handleCustomButtonClick}
                    currentDate={currentDate}
                    onChange={onChange}
                />

                {isCollapsed ? (
                    <span
                        className="flex items-center rounded-full bg-white p-2 hover:cursor-pointer hover:bg-gray-hover"
                        data-testid="dashboard-header-item-collapse"
                        onClick={onClick}
                    >
                        <ChevronRightIcon />
                    </span>
                ) : (
                    <>
                        <div className={clsx("mt-0.25 flex flex-grow items-center px-6", isCalendar && "print:hidden")}>
                            {isLoading ? (
                                <Skeleton
                                    height={45}
                                    classNames={{
                                        container: clsx(
                                            isList && "bg-blue-lighter",
                                            "px-4 !justify-start !border-none ",
                                        ),
                                    }}
                                >
                                    <div className={clsx(isList && "text-black", "mr-3 ")}>
                                        Loading data
                                        <Spinner className={clsx(isList && "text-black", "p-2")} />
                                    </div>
                                </Skeleton>
                            ) : (
                                <TimeslotsStatusCounts
                                    availableCount={availableCount}
                                    reservedCount={reservedCount}
                                    waitlistCount={waitlistCount}
                                    holdStatusCount={holdStatusCount}
                                />
                            )}
                        </div>
                        {isTable && tableGridRef && (
                            <TableColumnCustomizationAndPrint
                                tableGridRef={tableGridRef}
                                visibleExperiences={visibleExperiences}
                                isLoading={isLoading}
                            />
                        )}
                        {isTableOrCalendar && (
                            <div className={clsx(isCalendar && "print:hidden")}>
                                <SecondaryButton
                                    size="small"
                                    className="text-bold ml-auto mr-2 text-sm"
                                    onClick={() => handleCustomButtonClick?.("today")}
                                >
                                    Today
                                </SecondaryButton>
                            </div>
                        )}
                        {reservedCount > 0 && isDay && (
                            <div>
                                <RosterOptions
                                    setShowMessageDayEventsModal={setShowMessageDayEventsModal}
                                    setShowExportRosterModal={setShowExportRosterModal}
                                />

                                <MessageDayEventsModal
                                    date={date}
                                    eventIds={eventIds}
                                    isOpen={showMessageDayEventsModal}
                                    onClose={() => setShowMessageDayEventsModal(false)}
                                />
                                <ExportRosterModal
                                    filters={{
                                        format: "roster",
                                        reserved: true,
                                        // These are UTC timestamps because the events endpoint uses UTC times as per Event.start
                                        start: dayjs.utc(date).startOf("day").unix(),
                                        end: dayjs.utc(date).endOf("day").unix(),
                                    }}
                                    eventId={timeslots.map((timeslot) => timeslot.event?.id)}
                                    isOpen={showExportRosterModal}
                                    onClose={() => setShowExportRosterModal(false)}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>
        );
    },
);

interface DateNavProps {
    isCollapsed: boolean;
    handleCustomButtonClick?: (direction: "next" | "prev" | "today") => void;
    onChange?: (date: Date) => void;
    currentDate: dayjs.Dayjs;
}

const DateNav = ({ isCollapsed, handleCustomButtonClick, onChange, currentDate }: DateNavProps) => {
    const mode = useDashboardFilterStore((state) => state.filters.mode);
    const { dateText, widthClass } = getDateTextAndClassNamesForMode(mode, currentDate, isCollapsed);
    const isDay = isDayView();
    const isCalendar = isCalendarView();
    const isTableOrCalendar = showDateNavButtons();
    const isList = isListView();
    return (
        <div className={clsx("flex h-7.5 flex-row items-center text-lg", isCollapsed ? "space-x-4" : "space-x-6")}>
            {isTableOrCalendar && (
                <div data-testid="dashboard-header-item-prev-button" className={clsx(isCalendar && "print:hidden")}>
                    <ChevronLeftIcon
                        className="cursor-pointer"
                        size="medium"
                        onClick={() => handleCustomButtonClick?.("prev")}
                    />
                </div>
            )}
            <div
                className={clsx(
                    "ui-dashboard-header-item-date text-center font-bold",
                    !isList && "cursor-pointer underline-offset-4 hover:underline",
                    !isList && widthClass,
                )}
            >
                {/* need to show Datepicker in popover attached to body to avoid sticky header not to show on top of Datepicker */}
                {isList ? (
                    <NoTranslate as="div">{dateText}</NoTranslate>
                ) : (
                    <DashboardDatePickerPopover date={currentDate} onChange={onChange}>
                        <NoTranslate as="div">{dateText}</NoTranslate>
                    </DashboardDatePickerPopover>
                )}
            </div>
            {isTableOrCalendar && (
                <div data-testid="dashboard-header-item-next-button" className={clsx(isCalendar && "print:hidden")}>
                    <ChevronRightIcon
                        className="cursor-pointer"
                        size="medium"
                        onClick={() => handleCustomButtonClick?.("next")}
                    />
                </div>
            )}
            <Line mode={mode} />
            {isDay && (
                <div>
                    <NoTranslate
                        as="div"
                        className={clsx("ui-dashboard-header-item-day min-w-10 text-center font-bold")}
                    >
                        {currentDate?.format(DateFormat.DAY)}
                    </NoTranslate>
                </div>
            )}
            {isDay && !isCollapsed && <Line mode={mode} />}
        </div>
    );
};

const Line = ({ mode }: { mode: DashboardMode }) => {
    return (
        <div
            className={clsx(
                "h-full w-0.25 border-r",
                mode === DashboardMode.LIST ? "border-blue-darker/20" : "border-gray-dark/30",
            )}
        />
    );
};

const getDateTextAndClassNamesForMode = (mode: DashboardMode, currentDate: dayjs.Dayjs, isCollapsed: boolean) => {
    const currentYear = dayjs().year();
    const isNotCurrentYear = currentDate.year() !== currentYear;

    const formatWithOptionalYear = (date: dayjs.Dayjs, includeYear: boolean, dateFormat?: DateFormat) =>
        `${date.format(dateFormat ?? DateFormat.DATE_MONTH)}${includeYear ? `, ${date.format(DateFormat.YEAR)}` : ""}`;

    let dateText = "";
    let widthClass = "";
    switch (mode) {
        case DashboardMode.DAY:
        case DashboardMode.LIST:
        case DashboardMode.EQUIPMENT:
        case DashboardMode.TABLE:
            dateText = formatWithOptionalYear(currentDate, isNotCurrentYear && !isCollapsed);
            widthClass = isNotCurrentYear ? "min-w-32" : "min-w-16";
            break;
        case DashboardMode.WEEK: {
            const { start, end } = getWeekStartEndRange(currentDate);
            const includeYear = start.year() !== currentYear || end.year() !== currentYear;
            dateText = `${formatWithOptionalYear(start, includeYear)} - ${formatWithOptionalYear(end, includeYear)}`;
            widthClass = includeYear ? "min-w-65" : "min-w-36";
            break;
        }
        case DashboardMode.MONTH:
            dateText = formatWithOptionalYear(currentDate, isNotCurrentYear, DateFormat.MONTH);
            widthClass = isNotCurrentYear ? "min-w-24" : "min-w-12";
            break;
    }
    return { dateText, widthClass };
};

const TimeslotsStatusCounts = ({
    availableCount,
    reservedCount,
    holdStatusCount,
    waitlistCount,
}: {
    availableCount: number;
    reservedCount: number;
    holdStatusCount: number;
    waitlistCount: number;
}) => {
    return (
        <>
            <span className="ui-dashboard-header-item-available min-w-20">
                <var data-var="availability">
                    {availableCount >= OPEN_UNLIMITED ? (
                        <InfinityIcon />
                    ) : (
                        <span className={clsx(availableCount <= 0 && "text-danger")}>{availableCount}</span>
                    )}
                </var>
                &nbsp;Available
            </span>

            <span className="mx-2 text-3xl opacity-20">&middot;</span>
            <span className="ui-dashboard-header-item-reserved min-w-20">
                <var data-var="reservedCount">{reservedCount}</var> Reserved
            </span>
            {holdStatusCount > 0 && (
                <>
                    <span className="mx-2 text-3xl opacity-20">&middot;</span>
                    <span className="min-w-20">
                        <var data-var="holdStatusCount">{holdStatusCount}</var> Purchasing
                    </span>
                </>
            )}
            {waitlistCount > 0 && (
                <>
                    <span className="mx-2 text-3xl opacity-20">&middot;</span>
                    <span className="ui-dashboard-header-item-waitlist min-w-20">
                        <var data-var="waitlistCount">{waitlistCount}</var> Waitlisted
                    </span>
                </>
            )}
        </>
    );
};

interface RosterOptionsProps {
    setShowMessageDayEventsModal: (value: boolean) => void;
    setShowExportRosterModal: (value: boolean) => void;
}
const RosterOptions = ({ setShowMessageDayEventsModal, setShowExportRosterModal }: RosterOptionsProps) => {
    const { isGranted } = useAuthorization();
    const isMessageDayReservationVisible = isGranted(Permission.MESSAGE_DAY_RESERVATION_VIEW);

    return (
        <Popover placement="bottom" appendTo={document.body} trigger="click">
            <SecondaryButton className="mr-2" size="small">
                <EllipsisIcon />
            </SecondaryButton>

            <Popover.Content>
                <Menu>
                    {isMessageDayReservationVisible && (
                        <Menu.Item icon={<CommentIcon />} onClick={() => setShowMessageDayEventsModal(true)}>
                            Message Day&apos;s Reservations
                        </Menu.Item>
                    )}

                    <Menu.Item icon={<ExportIcon />} onClick={() => setShowExportRosterModal(true)}>
                        Export Day&apos;s Roster
                    </Menu.Item>
                </Menu>
            </Popover.Content>
        </Popover>
    );
};

interface ColumnCustomizationAndPrintProps {
    tableGridRef: React.RefObject<AgGridReact>;
    visibleExperiences?: Experience[];
    isLoading?: boolean;
}

const TableColumnCustomizationAndPrint = ({
    tableGridRef,
    visibleExperiences,
    isLoading,
}: ColumnCustomizationAndPrintProps) => {
    const { showPrintDashboardTable } = useDashboardTablePrint();

    return (
        <>
            <DashboardTableConfiguration gridRef={tableGridRef} visibleExperiences={visibleExperiences ?? []} />
            <SecondaryButton
                size="small"
                className="text-bold ml-auto mr-2 items-center px-1.5 text-sm"
                disabled={isLoading}
                onClick={() => {
                    showPrintDashboardTable?.();
                    setTimeout(() => {
                        /** We need to perform print after a little delay, so the state and content needed to be print, would have rendered */
                        window.print();
                    }, 100);
                }}
            >
                {isLoading ? <Spinner size="tiny" /> : <PrintIcon size="medium" />}
            </SecondaryButton>
        </>
    );
};
